import type React from 'react';

import { DetectionIcon } from '@vlabs/icons';
import { calcSimilarityPercent } from '@vlabs/shared/utils';
import { Control, FaceSample, Tag } from '@vlabs/uikit';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useGetFace, usePatchFace } from '@vlabs/api/luna-api/client';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import st from './AttachSimilarFaceToCard.module.sass';
import type { FaceCandidateMatchResult, FaceId } from '@vlabs/api/luna-api/model';
import type { CardId } from '@vlabs/api/point-api/model';
import { CardSample } from '../../../shared/ui/card-sample';
import { GoToCardCircleAnimatedButton } from '../../go-to-card';

interface SimilarFaceProps extends WithDataTestid {
  faceId: FaceId;
  similarityFace: Required<FaceCandidateMatchResult>;
  onSuccess?: VoidFunction;
}

const displayName = 'AttachSimilarFaceToCard';
const testid = buildTestid(displayName);

function AttachSimilarFaceToCard({
  faceId,
  similarityFace,
  onSuccess = undefined,
  'data-testid': dataTestId = undefined,
}: SimilarFaceProps): React.JSX.Element {
  const { t } = useTranslation();
  const getFace = useGetFace(faceId, { targets: ['avatar'] });

  const { mutate: attachFaceToCard, isPending } = usePatchFace({
    mutation: {
      onSuccess: async () => {
        toast.success(t('features:attach_similar_face_to_card.toast.success'));
        onSuccess?.();
      },
      onError: () => {
        toast.error(t('widgets:attach_face_to_card_button.toast_error'));
      },
    },
  });

  return (
    <div className={st.Root}>

      <div className={st.Header}>
        <span className={st.Title}>
          {t('features:attach_similar_face_to_card.title')}
        </span>

        <span className={st.Subtitle}>
          {t('features:attach_similar_face_to_card.subtitle')}
        </span>
      </div>

      <div className={st.Body}>
        <div className={st.SamplesWrapper}>
          <Tag
            className={st.SimilarityTag}
            icon={<DetectionIcon />}
            kind="positive"
            size="large"
            value={calcSimilarityPercent(similarityFace.similarity) as string}
          />

          <div className={st.Sample}>
            <FaceSample
              className={st.FaceSample}
              size="large"
              src={getFace.data?.avatar ? `/api/lp5${getFace.data.avatar}` : undefined}
            />
            <span className={st.Discription}>
              {t('features:attach_similar_face_to_card.face')}
            </span>
          </div>

          <div className={st.Sample}>
            <CardSample
              buttonOnBottom={(
                <GoToCardCircleAnimatedButton
                  cardId={similarityFace.face.external_id as CardId}
                  size="small"
                />
              )}
              cardId={similarityFace.face.external_id as CardId}
              className={st.FaceSample}
              size="large"
            />
            <span className={st.Discription}>
              {t('features:attach_similar_face_to_card.card')}
            </span>
          </div>
        </div>
      </div>

      <Control.Button
        className={st.ButtonWrapper}
        disabled={isPending}
        onClick={() => attachFaceToCard({
          faceId,
          data: { external_id: similarityFace.face.external_id },
        })}
        {...testid(dataTestId)}
      >
        {t('features:attach_similar_face_to_card.buttons.add')}
      </Control.Button>
    </div>
  );
}

AttachSimilarFaceToCard.displayName = displayName;

export { AttachSimilarFaceToCard };
